// ============================================================
// geolocation — 打卡用的手機定位（首頁與打卡頁共用）
// ------------------------------------------------------------
// 【為什麼不直接用 getCurrentPosition】
// 1. 首頁原本設 maximumAge: 60000，會沿用最多 60 秒前的舊位置——
//    員工走進店裡前一分鐘，手機記到的位置還在路上。
// 2. iPhone／Android 的第一筆定位常常是粗略的（Wi-Fi／基地台，誤差數百公尺），
//    多等幾秒才會收斂到衛星定位。只取第一筆就送出，等於拿最不準的那筆去比。
// 3. 原本所有錯誤都吞掉回 null，員工看不到「其實是權限被拒」。
//
// 做法：watchPosition 最多等 10 秒，期間保留誤差最小的一筆；
// 誤差 ≤ 50 公尺就提早結束。誤差（accuracy）一併送給後端，
// 後端會把它算進圍籬判定（見 backend AttendanceVerifier）。
// ============================================================

export interface GeoFix {
  latitude: number;
  longitude: number;
  /** 手機回報的誤差（公尺） */
  accuracy: number;
}

export type GeoErrorKind = 'unsupported' | 'denied' | 'timeout' | 'unavailable';

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);

const MESSAGES: Record<GeoErrorKind, string> = {
  unsupported: '這支手機的瀏覽器不支援定位，請到櫃檯打卡站打卡，或掃描店內 QR',
  denied: isIOS
    ? '定位權限被關閉了。請到 iPhone「設定 → 隱私權與安全性 → 定位服務 → Safari 網站」，選「使用 App 期間」並打開「精確位置」，再重新打卡。或直接到櫃檯打卡站打卡'
    : '定位權限被關閉了。請點網址列左邊的鎖頭圖示 →「網站設定」→「位置」→ 允許，再重新打卡。或直接到櫃檯打卡站打卡',
  timeout: '等了 10 秒還抓不到位置（室內收訊不好）。請到櫃檯打卡站打卡，或掃描店內 QR',
  unavailable: '手機目前抓不到位置。請確認定位服務已開啟，或直接到櫃檯打卡站打卡',
};

export class GeoError extends Error {
  constructor(public kind: GeoErrorKind) {
    super(MESSAGES[kind]);
    this.name = 'GeoError';
  }
}

export function getBestPosition(
  { maxWaitMs = 10_000, goodEnoughM = 50 }: { maxWaitMs?: number; goodEnoughM?: number } = {}
): Promise<GeoFix> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      reject(new GeoError('unsupported'));
      return;
    }

    let best: GeoFix | null = null;
    let sawUnavailable = false;
    let settled = false;
    let watchId: number | null = null;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      fn();
    };

    // 期限到：有任何一筆就用最好的那筆，沒有就算逾時
    const timer = setTimeout(() => {
      finish(() => (best ? resolve(best) : reject(new GeoError(sawUnavailable ? 'unavailable' : 'timeout'))));
    }, maxWaitMs);

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const fix = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
        if (!best || fix.accuracy < best.accuracy) best = fix;
        if (fix.accuracy <= goodEnoughM) finish(() => resolve(fix));
      },
      (err) => {
        // 1 權限被拒：等下去也不會有結果，立刻回報
        if (err.code === err.PERMISSION_DENIED) {
          finish(() => reject(new GeoError('denied')));
          return;
        }
        // 3 單次逾時：已有任何一筆就用它
        if (err.code === err.TIMEOUT && best) {
          finish(() => resolve(best!));
          return;
        }
        // 2 暫時抓不到：繼續等到期限，期限到仍沒有就回報
        if (err.code === err.POSITION_UNAVAILABLE) { sawUnavailable = true; return; }
        finish(() => reject(new GeoError(err.code === err.TIMEOUT ? 'timeout' : 'unavailable')));
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: maxWaitMs }
    );
  });
}
